// Soorten huishouden (0043_huishoudtypes; feedback oprichter 26 sep: "ik wil dit niet alleen voor
// gezinnen ... studentenwoning ... eenpersoonhuishouden"; docs/onderzoek/feedback-26-sep.md §3).
//
// Eén keuze per huishouden bepaalt drie dingen, en verder niets:
//   1. welke modules standaard aan, uit of verborgen zijn (tabel §3.2, hieronder PRESETS);
//   2. het thema (tokens: kleur, beeld, tekstgrootte) van de webapp en het bord;
//   3. een paar woorden (i18n: "Huisgenoten" in plaats van "Gezinsleden").
// De keuze van het huishouden zelf (een rij in household_module) wint altijd. Van soort wisselen
// verandert dus alleen modules zonder eigen keuze, en wist nooit gegevens: een module die uit gaat,
// houdt alles bewaard tot hij weer aan gaat.
//
// Puur en zonder Zod: ook de webapp importeert dit (`@amumin/domain/modules`), de kiosk alleen types.
/**
 * De soorten, in de volgorde van de vraag "Wie wonen er in huis?" (`care` achteraan: die komt uit
 * de vervolgvraag bij Ouderen thuis). Integratie golf 6b live r1, oprichter 27 sep: "zet de
 * verschillende vormen van samenstelling huishouden waar we het eerder over hadden zoals
 * studentenwoning of ouderenwoning erbij" (docs/onderzoek/feedback-26-sep.md §3.1):
 *   - `student`: studentenhuis (zoals huisgenoten: rooster, schoonmaak, boodschappen, berichten);
 *   - `cohousing`: woongroep of hofje, eigen woningen plus een gedeelde ruimte (één huishouden
 *     voor de gedeelde ruimte: agenda van de gemeenschappelijke ruimte, berichten, schoonmaak);
 *   - `senior`: ouderen die zelfstandig thuis wonen (groot en hoog contrast, afspraken,
 *     herinneringen ook voor medicijnen, weer); met familie die op afstand meekijkt: `care`.
 */
export const HOUSEHOLD_TYPES = [
    'family',
    'two_home',
    'couple',
    'solo',
    'student',
    'shared',
    'cohousing',
    'senior',
    'care',
];
/** Bestaande huishoudens (en een onbekende waarde) zijn een gezin: niets verandert voor hen. */
export const DEFAULT_HOUSEHOLD_TYPE = 'family';
export function isHouseholdType(value) {
    return typeof value === 'string' && HOUSEHOLD_TYPES.indexOf(value) >= 0;
}
export function householdTypeOf(value) {
    return isHouseholdType(value) ? value : DEFAULT_HOUSEHOLD_TYPE;
}
/** Soorten met kinderen in huis: daar horen Beloningen, Wensen en Brengen en halen bij. */
export function isFamilyType(type) {
    return type === 'family' || type === 'two_home';
}
/**
 * Een huis met huisgenoten (huisgenoten, studentenhuis): het beurtrooster als hart van het huis, het
 * bord in de gang (van 2 tot 3 meter te lezen) en geen illustraties.
 */
export function isSharedHouseType(type) {
    return type === 'shared' || type === 'student';
}
/** Groot en hoog contrast (thema care-high-contrast): ouderen thuis, met of zonder familie. */
export function isLargeTextType(type) {
    return type === 'senior' || type === 'care';
}
/**
 * Ingebouwde klusjes-ideeën die alleen bij kinderen horen (0009_tasks_points): tanden poetsen,
 * aankleden, speelgoed en schoenen opruimen, schooltas, huiswerk, lezen, handdoek ophangen,
 * pyjama aan. Bij volwassen soorten (stel, alleen, studentenhuis, huisgenoten, woongroep, ouderen
 * thuis, zorg) staan ze niet tussen de
 * ideeën (fixer golf 6b r1: "niet zo kinderlijk bij een studentenwoning"); eigen sjablonen nooit.
 */
export const CHILD_TASK_TEMPLATE_IDS = new Set([
    '0192a000-0000-7000-8000-000000000001',
    '0192a000-0000-7000-8000-000000000002',
    '0192a000-0000-7000-8000-000000000004',
    '0192a000-0000-7000-8000-000000000010',
    '0192a000-0000-7000-8000-000000000011',
    '0192a000-0000-7000-8000-000000000012',
    '0192a000-0000-7000-8000-000000000013',
    '0192a000-0000-7000-8000-000000000023',
    '0192a000-0000-7000-8000-000000000024',
]);
/** Past dit ingebouwde klusjes-idee bij deze soort huishouden? */
export function taskTemplateFits(id, builtin, type) {
    return !builtin || isFamilyType(type) || !CHILD_TASK_TEMPLATE_IDS.has(id);
}
const A = { on: true };
const U = { on: false };
const H = { on: false, hidden: true };
/**
 * Tabel §3.2 (A = aan, u = uit, – = verborgen). Een module die hier niet staat, volgt
 * `default_on` uit het register. Gezin krijgt alleen H-regels voor de modules van golf 8 (Kosten
 * delen en Reserveren verborgen; Wie eet mee uit maar zichtbaar); verder geldt precies wat er vóór
 * 0043 gold (bestaande gezinnen merken niets).
 *
 * Golf 8 (0049_samen_wonen; docs/onderzoek/golf8-samenwonen-en-ouderen.md §4.3): Wie eet mee, Kosten
 * delen en Reserveren staan bij studentenhuis en huisgenoten aan (A), maar slapend tot het huis is
 * ingericht: geen melding, geen blok op het bord en geen widget met inhoud (§2.0.2). Bij de woongroep
 * staan eten en reserveren aan (Kosten delen uit maar zichtbaar); het agendabord blijft tot de
 * woongroep zelf "op het bord" aanzet. Stel en twee huizen: Kosten delen uit maar zichtbaar.
 */
export const HOUSEHOLD_TYPE_PRESETS = {
    // Golf 8: de check-in en de familiekring horen alleen bij ouderen (verborgen, uit).
    family: { expenses: H, bookings: H, check_in: H, family_circle: H },
    // Twee huizen: Kosten voor de kinderen (aanzetten vraagt een bevestiging in de webapp).
    two_home: { dinner_list: H, expenses: U, bookings: H, check_in: H, family_circle: H },
    couple: {
        dinner_list: H,
        expenses: U,
        bookings: H,
        rides: U,
        wishes: U,
        rooms: U,
        health: H,
        school: H,
        rewards: H,
        chore_rotation: U,
        // Integratie golf 6b: Beginscherm op maat hoort bij elke soort (ook met één persoon).
        widgets: A,
        check_in: H,
        family_circle: H,
    },
    solo: {
        dinner_list: H,
        expenses: H,
        bookings: H,
        rides: H,
        wishes: H,
        messages: U,
        rooms: H,
        health: H,
        school: H,
        languages: U,
        rewards: H,
        chore_rotation: H,
        widgets: A,
        check_in: H,
        family_circle: H,
    },
    // Studentenhuis (live r1): als huisgenoten. Rooster en schoonmaak, boodschappen, berichten en
    // agenda aan; geen beloningen, geen brengen en halen, geen kinderklusjes. Gebedstijden volgen het
    // profiel (per persoon, niet standaard op het bord).
    student: {
        // Golf 7: 'Soera's leren' hoort bij kinderen van een gezin (v1), net als bij huisgenoten.
        memorize: H,
        rides: H,
        travel_time: U,
        wishes: H,
        health: H,
        school: H,
        rewards: H,
        chore_rotation: A,
        widgets: A,
        dinner_list: A,
        expenses: A,
        bookings: A,
        check_in: H,
        family_circle: H,
    },
    shared: {
        // Golf 7: 'Soera's leren' hoort bij kinderen van een gezin (v1).
        memorize: H,
        rides: H,
        travel_time: U,
        wishes: H,
        health: H,
        school: H,
        rewards: H,
        chore_rotation: A,
        widgets: A,
        dinner_list: A,
        expenses: A,
        bookings: A,
        check_in: H,
        family_circle: H,
    },
    // Woongroep of hofje (live r1): het huishouden is de gedeelde ruimte. Agenda (de
    // gemeenschappelijke ruimte), berichten, kamers en schoonmaak van de gedeelde ruimtes aan; geen
    // beloningen, geen brengen en halen, geen kinderklusjes; geen beurtrooster tenzij ze dat willen.
    cohousing: {
        rides: H,
        travel_time: U,
        wishes: H,
        health: H,
        school: H,
        languages: U,
        rewards: H,
        chore_rotation: U,
        widgets: A,
        // Golf 8: "Gezamenlijke maaltijd" en Reserveren aan (slapend); Kosten delen kan aan.
        dinner_list: A,
        expenses: U,
        bookings: A,
        check_in: H,
        family_circle: H,
    },
    // Ouderen thuis (live r1): afspraken, herinneringen (ook medicijnen), weer en gebedstijden als het
    // profiel dat zegt; geen beloningen, geen brengen en halen, geen kinderklusjes. Als care, zonder
    // familie op afstand.
    senior: {
        memorize: H,
        rides: H,
        travel_time: U,
        place_search: U,
        wishes: H,
        rooms: H,
        cleaning: U,
        health: H,
        school: H,
        languages: U,
        rewards: H,
        chore_rotation: H,
        widgets: A,
        dinner_list: H,
        expenses: H,
        bookings: H,
        // Golf 8 (B2): zichtbaar maar uit; aan alleen via de wizard of Check-in en kring.
        check_in: U,
        family_circle: U,
    },
    care: {
        memorize: H,
        rides: U,
        travel_time: U,
        place_search: U,
        wishes: U,
        rooms: H,
        cleaning: U,
        health: H,
        school: H,
        languages: U,
        rewards: H,
        chore_rotation: H,
        widgets: A,
        dinner_list: H,
        expenses: H,
        bookings: H,
        // Golf 8 (B2): zichtbaar maar uit; aan alleen via de wizard of Check-in en kring.
        check_in: U,
        family_circle: U,
    },
};
export function presetOf(type, id) {
    return HOUSEHOLD_TYPE_PRESETS[type][id];
}
// ---- Thema per soort (§3.4) --------------------------------------------------------------------
/**
 * Tokens-pakket op `<html data-theme-pack>` (webapp en bord). `family-warm` is het huidige thema en
 * zet geen attribuut: bestaande gezinnen zien niets anders.
 */
export const THEME_PACKS = [
    'family-warm',
    'two-home-calm',
    'minimal',
    'shared-graphic',
    'care-high-contrast',
];
export const THEME_PACK_OF = {
    family: 'family-warm',
    two_home: 'two-home-calm',
    couple: 'minimal',
    solo: 'minimal',
    student: 'shared-graphic',
    shared: 'shared-graphic',
    // Rustig en neutraal (één accent), geen illustraties van kinderen.
    cohousing: 'minimal',
    // Groot en hoog contrast, zoals care.
    senior: 'care-high-contrast',
    care: 'care-high-contrast',
};
export function themePackOf(type) {
    return THEME_PACK_OF[type];
}
export const WORD_SET_OF = {
    family: 'family',
    two_home: 'family',
    couple: 'adults',
    solo: 'adults',
    student: 'shared',
    shared: 'shared',
    // Woongroep: volwassen woorden, met "Bewoners" en "woongroep" (i18n households.cohousingWords).
    cohousing: 'adults',
    senior: 'adults',
    care: 'care',
};
/** Tonen we kinderplaatjes (mascottes, dieren, sterren) standaard? Alleen bij gezinnen. */
export function playfulArt(type) {
    return isFamilyType(type);
}
//# sourceMappingURL=household.js.map